import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { formatQty } from '../../utils/quantityFormat';

interface Props {
  quantity: number;
  parLevel?: number;
  reorderPoint?: number;
  size?: 'small' | 'medium';
}

/**
 * Displays current stock quantity with colour-coded severity:
 * - green:  at or above parLevel (or parLevel not set)
 * - amber:  below parLevel but at or above reorderPoint
 * - red:    at or below reorderPoint (or quantity ≤ 0)
 */
const InventoryQuantityBadge: React.FC<Props> = ({
  quantity,
  parLevel,
  reorderPoint,
  size = 'small',
}) => {
  const getColor = (): { color: string; bg: string; tip: string } => {
    if (quantity <= 0) {
      return { color: '#cf222e', bg: '#ffebe9', tip: 'Out of stock' };
    }
    if (reorderPoint !== undefined && quantity <= reorderPoint) {
      return { color: '#cf222e', bg: '#ffebe9', tip: `At or below reorder point (${reorderPoint})` };
    }
    if (parLevel !== undefined && quantity < parLevel) {
      return { color: '#9a6700', bg: '#fef9c3', tip: `Below par level (${parLevel})` };
    }
    return { color: '#1a7f37', bg: '#dafbe1', tip: 'At or above par level' };
  };

  const { color, bg, tip } = getColor();

  return (
    <Tooltip title={tip} placement="top">
      <Chip
        label={formatQty(quantity)}
        size={size}
        sx={{
          fontWeight: 700,
          fontSize: size === 'small' ? '0.75rem' : '0.875rem',
          color,
          backgroundColor: bg,
          border: 'none',
          height: size === 'small' ? 22 : 28,
          minWidth: 36,
        }}
      />
    </Tooltip>
  );
};

export default InventoryQuantityBadge;
